import { useState, useRef, useCallback } from 'react'
import type { Position } from '../../robot-position-setup/types'

interface UseRobotPositionEditorProps {
    initialPositions: Position[]
    setPositions: React.Dispatch<React.SetStateAction<Position[]>>
    homeYaw: number
    setHomeYaw: (yaw: number) => void
    setMapRotation: (yaw: number) => void
    selectedPositionId: string
    handleSelectPositionFromCoordinate: (posId: string) => void
    setDirectionRotateMode: (val: boolean) => void
}

export function useRobotPositionEditor({
    initialPositions,
    setPositions,
    homeYaw,
    setHomeYaw,
    setMapRotation,
    selectedPositionId,
    handleSelectPositionFromCoordinate,
    setDirectionRotateMode,
}: UseRobotPositionEditorProps) {
    const [isEditingMode, setIsEditingMode] = useState<boolean>(false)
    const [isAddmode, setIsAddmode] = useState<boolean>(false)
    const [addFormState, setAddFormState] = useState<{
        name: string
        yaw: number
    }>({
        name: '',
        yaw: 0,
    })

    const [confirmState, setConfirmState] = useState<{
        isOpen: boolean
        title?: string
        description?: React.ReactNode
        confirmText?: string
        cancelText?: string
        variant?: 'danger' | 'info'
        onConfirm?: () => void
    }>({ isOpen: false })

    const editingSnapshotRef = useRef<{
        positions: Position[]
        homeYaw: number
    } | null>(null)

    const handleCancelEditOrAdd = useCallback(() => {
        setIsEditingMode(false)
        setIsAddmode(false)
        setDirectionRotateMode(false)

        if (editingSnapshotRef.current) {
            const { positions: snapshotPositions, homeYaw: snapshotHomeYaw } =
                editingSnapshotRef.current
            setPositions(JSON.parse(JSON.stringify(snapshotPositions)))
            setHomeYaw(snapshotHomeYaw)
            setMapRotation(snapshotHomeYaw)
        } else {
            setPositions(JSON.parse(JSON.stringify(initialPositions)))

            const originalHome = initialPositions.find((p) => p.id === 'home')
            const defaultYaw = originalHome && typeof originalHome.yaw === 'number' ? originalHome.yaw : 0

            setHomeYaw(defaultYaw)
            setMapRotation(defaultYaw)
        }

        editingSnapshotRef.current = null
    }, [
        initialPositions,
        setPositions,
        setHomeYaw,
        setMapRotation,
        setDirectionRotateMode,
    ])

    const executeWithConfirmIfBusy = useCallback(
        (action: () => void) => {
            if (isEditingMode || isAddmode) {
                const modeText = isAddmode ? 'thêm mới' : 'cập nhật'
                setConfirmState({
                    isOpen: true,
                    title: 'Hủy thao tác hiện tại?',
                    description: `Bạn đang trong quá trình ${modeText} vị trí. Bạn có chắc chắn muốn hủy bỏ thao tác này không?`,
                    confirmText: 'Đồng ý',
                    cancelText: 'Hủy',
                    variant: 'danger',
                    onConfirm: () => {
                        handleCancelEditOrAdd()
                        action()
                    },
                })
            } else {
                action()
            }
        },
        [isEditingMode, isAddmode, handleCancelEditOrAdd],
    )

    const handleSelectPositionWithModeReset = useCallback(
        (posId: string) => {
            if (selectedPositionId === posId && (isEditingMode || isAddmode)) return
            executeWithConfirmIfBusy(() => {
                handleSelectPositionFromCoordinate(posId)
                setIsEditingMode(false)
                setIsAddmode(false)
                setDirectionRotateMode(false)
            })
        },
        [
            selectedPositionId,
            isEditingMode,
            isAddmode,
            executeWithConfirmIfBusy,
            handleSelectPositionFromCoordinate,
            setDirectionRotateMode,
        ],
    )

    const handleStartUpdatePosition = useCallback(
        (posId: string, currentPositions: Position[]) => {
            executeWithConfirmIfBusy(() => {
                setIsAddmode(false)
                handleSelectPositionFromCoordinate(posId)

                editingSnapshotRef.current = {
                    positions: JSON.parse(JSON.stringify(currentPositions)),
                    homeYaw,
                }

                setIsEditingMode(true)
                setDirectionRotateMode(true)
            })
        },
        [
            executeWithConfirmIfBusy,
            handleSelectPositionFromCoordinate,
            homeYaw,
            setDirectionRotateMode,
        ],
    )

    const handleStartAddPosition = useCallback(
        (currentPositions?: Position[]) => {
            executeWithConfirmIfBusy(() => {
                if (currentPositions) {
                    editingSnapshotRef.current = {
                        positions: JSON.parse(JSON.stringify(currentPositions)),
                        homeYaw,
                    }
                }
                setIsEditingMode(false)
                setIsAddmode(true)
                setDirectionRotateMode(true)
                setAddFormState({
                    name: '',
                    yaw: 0,
                })
            })
        },
        [executeWithConfirmIfBusy, homeYaw, setDirectionRotateMode],
    )

    return {
        isEditingMode,
        setIsEditingMode,
        isAddmode,
        setIsAddmode,
        addFormState,
        setAddFormState,
        confirmState,
        setConfirmState,
        editingSnapshotRef,
        handleCancelEditOrAdd,
        executeWithConfirmIfBusy,
        handleSelectPositionWithModeReset,
        handleStartUpdatePosition,
        handleStartAddPosition,
    }
}